import { Grid, makeStyles } from '@material-ui/core';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikErrors } from 'formik';
import _ from 'lodash';
import React, { ReactElement, useContext, useState } from 'react';
import { useAsync } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  AdminDocumentMatchRequest,
  CountryResponse,
  DocumentCategory,
  DocumentDetailOptionResponse,
  DocumentLevel,
  useMurexinPimContextApi,
} from '../../../api';
import { NotificationContext } from '../../../contexts';
import { AdminBreadCrumbs } from '../../../layouts/AdminBreadCrumbs/AdminBreadCrumbs';
import { routes } from '../../../routes';
import MessageDialog from '../../../shared/Components/MessageDialog';
import PIMButton from '../../../shared/Components/PIMButton';
import { documentCategories, documentLevels } from '../../../shared/DocumentTranslations';
import { CircleLoader } from '../../../utils/CircleLoader';
import { DocumentsUploadGrid } from './DocumentsUploadGrid';

interface DocumentCreateRequest {
  countryCode: string;
  detailId: number | undefined;
  fileName: string;
  documentCategory: DocumentCategory;
  documentLevel: DocumentLevel;

  isExported: boolean | undefined;
  isKpExported: boolean | undefined;
  isNew: boolean;
}

export interface Document extends DocumentCreateRequest {
  file: File;
  detailName: string;
}

export type PropertyApplyForAll = {
  documentCategory: boolean;
  countryCode: boolean;
  documentLevel: boolean;
  documentDetail: boolean;
};

interface DropdownOption {
  key: string;
  value: string;
}

interface DocumentDetailDropdownOption extends DropdownOption {
  documentLevel: DocumentLevel;
}

export interface DropdownOptions {
  countryTypeOptions: DropdownOption[];
  documentCategoryOptions: DropdownOption[];
  documentDetailOptions: DocumentDetailDropdownOption[];
  documentLevelOptions: DropdownOption[];
}

type IFormikValues = {
  documents: Document[];
  applyForAll: PropertyApplyForAll;
};

export function DocumentsUpload(): ReactElement {
  const { t } = useTranslation();
  const api = useMurexinPimContextApi();
  const classes = useStyles();
  const headerDocument = t('Dokumente');
  const headerDocumentUpload = t('Dokumente hochladen');
  const defaultCountryCode = 'AT';
  const notification = useContext(NotificationContext);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const countries = useAsync(async () => {
    const countryListResponse = await api.countryService.countryServiceList();
    return countryListResponse.data ?? [];
  }, []);

  const details = useAsync(async () => {
    const result = await api.admin.adminDocumentAdminDetailsList();
    return result?.data ?? [];
  }, []);

  const options = {
    countryTypeOptions: countries.result?.map((country: CountryResponse) => ({
      key: country.code,
      value: country.name ?? '',
    })),

    documentCategoryOptions: [...documentCategories].map((category) => ({
      key: category.id,
      value: category.name,
    })),

    documentDetailOptions: details.result?.map((detail: DocumentDetailOptionResponse) => ({
      key: detail.detailName,
      value: detail.detailDescription,
      documentLevel: detail.documentLevel,
    })),

    documentLevelOptions: [...documentLevels]?.map((level) => ({
      key: level.id,
      value: level.name,
    })),
  } as DropdownOptions;

  const addFiles = async (files: File[], arrayHelpers: FieldArrayRenderProps, documents: Document[]) => {
    const duplicateFiles = findDuplicateFilesByName(files, documents);
    if (duplicateFiles.length > 0) {
      notification.error(t('Datei bereits hochgeladen', { fileName: duplicateFiles[0].name }));
      return;
    }

    const addedDocumentsFromFiles = getDocumentsFromFiles(files);

    try {
      const documentMatches = await api.admin.adminDocumentAdminMatchesCreate(
        addedDocumentsFromFiles as AdminDocumentMatchRequest[]
      );

      documentMatches.data.forEach((documentMatch) => {
        const document = addedDocumentsFromFiles.find(
          (f) => f.fileName.toLocaleLowerCase() === documentMatch.fileName.toLocaleLowerCase()
        );

        if (document) {
          document.isNew = false;
          document.countryCode = documentMatch.countryCode;
          document.documentLevel = documentMatch.documentLevel;
          document.detailName = documentMatch.detailName;
          document.detailId = documentMatch.detailId;
          document.documentCategory = documentMatch.documentCategory;
          document.isExported = documentMatch.isExported;
          document.isKpExported = documentMatch.isKpExported;
        }
      });

      addedDocumentsFromFiles.forEach((document) => {
        arrayHelpers.push(document);
      });
    } catch (error) {
      notification.error(t('Error matching existing documents'));
    }
  };

  const handleOnSubmit = async (formikValues: IFormikValues, resetForm: () => void) => {
    try {
      const files = formikValues.documents.map((document) => document.file);
      const documentRequest = mapDocumentRequest(formikValues.documents, formikValues.applyForAll);
      await api.admin.adminDocumentAdminCreate({
        documentRequestsJson: JSON.stringify(documentRequest),
        files,
      });
      resetForm();
      notification.success(t('Hochladen erfolgreich'));
    } catch (error) {
      notification.error(t('Hochladen fehlgeschlagen'));
    }
  };

  const mapDocumentRequest = (documents: Document[], applyForAll: PropertyApplyForAll): DocumentCreateRequest[] => {
    const first = documents[0];

    return documents.map((document) => {
      return document.isNew
        ? ({
            ...document,
            documentLevel: applyForAll.documentLevel ? first.documentLevel : document.documentLevel,
            documentCategory: applyForAll.documentCategory ? first.documentCategory : document.documentCategory,
            countryCode: applyForAll.countryCode ? first.countryCode : document.countryCode,
            detailId: getDetailIdByName(applyForAll.documentDetail ? first.detailName : document.detailName),
            isExported: document.documentCategory === DocumentCategory.ExternalLayout ? false : document.isExported,
          } as DocumentCreateRequest)
        : document;
    });
  };

  const getDetailIdByName = (detailName: string) => details.result?.find((d) => d.detailName === detailName)?.detailId;

  function deleteDocument(document: Document, arrayHelpers: FieldArrayRenderProps, documents: Document[]) {
    arrayHelpers.remove(documents.indexOf(document));
  }

  function getDocumentsFromFiles(files: File[]): Document[] {
    const documentsFromFiles = files.map((file) => {
      let countryCode = defaultCountryCode;
      const splitFilenameWithoutExtension = file.name.split('.')[0].split('_');

      if (splitFilenameWithoutExtension.length > 1) {
        const countryCodeWithoutParentheses = splitFilenameWithoutExtension[
          splitFilenameWithoutExtension.length - 1
        ].replace(/[()']+/g, '');
        if (countries.result?.find((country) => country.code === countryCodeWithoutParentheses))
          countryCode = countryCodeWithoutParentheses;
      }

      return {
        isExported: false,
        isKpExported: false,
        file,
        countryCode,
        fileName: file.name,
        isNew: true,
      } as Document;
    });

    return documentsFromFiles;
  }

  const findDuplicateFilesByName = (files: File[], documents: Document[]) =>
    files.filter((file) => documents.find((d) => d.fileName === file.name));

  const createValidationSchema = (applyForAll: PropertyApplyForAll) => {
    return Yup.object().shape({
      documents: Yup.array().of(
        Yup.object().shape({
          documentCategory: Yup.mixed().test('documentCategory-conditional', 'Feld erforderlich', function (value) {
            const match = /\[(\d+)\]/.exec(this.path ?? '');
            const index = match?.[1];
            const documentIndex = index ? parseInt(index) : 0;

            // If applyForAll is true and this is not the first document, skip validation
            if (applyForAll.documentCategory && documentIndex > 0) {
              return true;
            }

            // Otherwise, require the value
            return !!value;
          }),

          countryCode: Yup.string().test('countryCode-conditional', 'Feld erforderlich', function (value) {
            const match = /\[(\d+)\]/.exec(this.path ?? '');
            const index = match?.[1];
            const documentIndex = index ? parseInt(index) : 0;

            if (applyForAll.countryCode && documentIndex > 0) {
              return true;
            }

            return !!value;
          }),

          fileName: Yup.string().required('Feld erforderlich'), // Always required

          detailName: Yup.string().test('detailName-conditional', 'Feld erforderlich', function (value) {
            const match = /\[(\d+)\]/.exec(this.path ?? '');
            const index = match?.[1];
            const documentIndex = index ? parseInt(index) : 0;

            if (applyForAll.documentDetail && documentIndex > 0) {
              return true;
            }

            return !!(value && value.trim().length > 0);
          }),

          documentLevel: Yup.mixed().test('documentLevel-conditional', 'Feld erforderlich', function (value) {
            const match = /\[(\d+)\]/.exec(this.path ?? '');
            const index = match?.[1];
            const documentIndex = index ? parseInt(index) : 0;

            if (applyForAll.documentLevel && documentIndex > 0) {
              return true;
            }

            return !!value;
          }),

          isExported: Yup.boolean().nullable(),
          isKpExported: Yup.boolean().nullable(),
        })
      ),
      applyForAll: Yup.object().shape({
        documentCategory: Yup.boolean(),
        countryCode: Yup.boolean(),
        documentLevel: Yup.boolean(),
        documentDetail: Yup.boolean(),
      }),
    });
  };

  const initialFormValues: IFormikValues = {
    documents: new Array<Document>(),
    applyForAll: {
      documentCategory: false,
      countryCode: false,
      documentLevel: false,
      documentDetail: false,
    } as PropertyApplyForAll,
  };

  if (countries.loading || details.loading) return <CircleLoader open />;
  else
    return (
      <>
        <AdminBreadCrumbs
          sites={[headerDocument, headerDocumentUpload]}
          links={[routes.DocumentsManagement(), routes.DocumentsUpload()]}
        />
        <Grid container className={classes.Container}>
          <Formik
            initialValues={initialFormValues}
            validate={(values) => {
              try {
                createValidationSchema(values.applyForAll).validateSync(values, { abortEarly: false });
                return {};
              } catch (err: any) {
                const errors: FormikErrors<IFormikValues> = {};
                if (err.inner && Array.isArray(err.inner)) {
                  err.inner.forEach((e: Yup.ValidationError) => {
                    if (e.path) {
                      _.set(errors, e.path, e.message);
                    }
                  });
                }
                return errors;
              }
            }}
            onSubmit={async (values, { resetForm }) => {
              await handleOnSubmit(values, resetForm);
            }}
            enableReinitialize>
            {({ values, isSubmitting, handleSubmit, resetForm, dirty, errors, setTouched, touched }) => {
              return (
                <>
                  <Form className={classes.Form}>
                    <Grid item xs={12} className={classes.MainPanel}>
                      <Grid container item xs={12} className={classes.HeaderRow}>
                        <Grid container item xs={6} justifyContent={'flex-start'}>
                          <div className={classes.Title}>{headerDocument}</div>
                        </Grid>
                        <Grid container item xs={6} justifyContent={'flex-end'}>
                          <PIMButton
                            icon='close'
                            color='secondary'
                            className={classes.TopButtons}
                            disabled={!dirty}
                            onClick={() => {
                              resetForm();
                              notification.info(t('Daten zurückgesetzt'));
                            }}>
                            {t('Abbrechen')}
                          </PIMButton>
                          <PIMButton
                            icon='up'
                            color='primary'
                            busy={isSubmitting}
                            onClick={async () => {
                              const touchedState = {
                                documents: values.documents.map(() => ({
                                  documentCategory: true,
                                  countryCode: true,
                                  documentLevel: true,
                                  detailName: true,
                                  fileName: true,
                                  isExported: true,
                                  isKpExported: true,
                                  isNew: true,
                                })),
                              };

                              await setTouched(touchedState);

                              if (Object.keys(errors).length === 0) {
                                setShowUploadDialog(true);
                              }
                            }}
                            className={classes.TopButtons}
                            disabled={values.documents.length === 0}>
                            {t('Hochladen')}
                          </PIMButton>
                        </Grid>
                      </Grid>
                      <Grid container className={classes.Panel} spacing={2}>
                        <FieldArray
                          name='documents'
                          render={(arrayHelpers) => {
                            return (
                              <DocumentsUploadGrid
                                documents={values.documents}
                                fieldArrayPrefix={arrayHelpers.name}
                                applyForAll={values.applyForAll}
                                options={options}
                                details={details.result ?? []}
                                deleteDocument={(document) => deleteDocument(document, arrayHelpers, values.documents)}
                                addDocuments={(files) => addFiles(files, arrayHelpers, values.documents)}
                              />
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Form>
                  <MessageDialog
                    show={showUploadDialog}
                    messageText={t('FrageDokumenteHochladen', {
                      newDocumentsCount: values.documents.filter((d) => d.isNew).length,
                      existingDocumentsCount: values.documents.filter((d) => !d.isNew).length,
                    })}
                    title={t('Dokumente hochladen')}
                    confirmLabel={t('Hochladen')}
                    onClose={() => setShowUploadDialog(false)}
                    onCancel={() => setShowUploadDialog(false)}
                    confirmIcon='up'
                    onConfirm={() => {
                      setShowUploadDialog(false);
                      handleSubmit();
                    }}
                  />
                </>
              );
            }}
          </Formik>
        </Grid>
      </>
    );
}

const useStyles = makeStyles(() => ({
  Container: {
    marginTop: '40px',
    paddingLeft: '16px',
    height: '100%',
  },
  MainPanel: {
    paddingRight: '24px',
  },
  Title: {
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '36px',
    marginBottom: '20px',
  },
  HeaderRow: {
    flexWrap: 'nowrap',
    marginBottom: '24px',
  },
  TopButtons: {
    marginLeft: '24px',
  },
  Panel: {
    background: 'white',
    padding: '16px 24px',
    margin: '16px 0px',
    overflowX: 'scroll',
  },
  Form: {
    width: '100%',
  },
}));
